import React, { FC, useCallback, useContext, useMemo } from 'react';
import { IApiConfig, IApiExecutor, useApi } from '../hooks/useApi';
import { PermissionContext } from '../contexts/PermissionContext';
import { EResourceMethod } from './models/IPermission';
import { useMemoWithEqualityCheck } from '../hooks/useMemoWithEqualityCheck';
import { EPolicyCondition, EPolicyOperator, IPolicy } from './models/IPolicy';
import { IRole } from './models/IRole';
import { IBasicOrganization } from './models/IOrganization';
import { IBasicUserInfo } from './models/IUserInfo';
import { OrganizationContext } from '../contexts/OrganizationContext';
import { CurrentUserContext } from '../contexts/CurrentUserContext';
import { AxiosRequestConfig } from 'axios';

type Obj = Record<string, any>;

export type TPayloadMethod = EResourceMethod.Post | EResourceMethod.Put;

export interface IResource<ParamsType extends Obj, MethodType extends EResourceMethod> {
  template: string;
  method: MethodType;
  params: ParamsType;
}

export interface IResourceData<
  ResourceType extends IResource<any, any>,
  ResponseType,
  InputPayloadType extends any[] = never,
  PayloadType = never
> {
  preparePayload: ResourceType extends IResource<any, TPayloadMethod>
    ? (...input: InputPayloadType) => PayloadType
    : never;
  prepareResponse: (body: any) => ResponseType;
}

interface IResourceGuardProps {
  resource: IResource<any, EResourceMethod> | null;
}

export interface IResourceBuilder<
  InputParamsType extends any[],
  ParamsType extends Obj,
  MethodType extends EResourceMethod
> {
  (...params: InputParamsType): IResource<ParamsType, MethodType>;
}

export const createResourceBuilder = <
  MethodType extends EResourceMethod,
  ParamsType extends Obj = Record<string, never>,
  InputParamsType extends any[] = []
>(
  template: string,
  method: MethodType,
  paramsFn: (...input: InputParamsType) => ParamsType = () => ({} as ParamsType)
): IResourceBuilder<InputParamsType, ParamsType, MethodType> => {
  return (...params: InputParamsType) => ({
    template,
    method,
    params: paramsFn(...params),
  });
};

class ResourcePolicy {
  constructor(private json: IPolicy) {}

  public examine(
    resource: IResource<any, EResourceMethod>,
    roles: IRole[],
    currentOrganization: IBasicOrganization | null,
    self: IBasicUserInfo | null
  ) {
    switch (this.json.condition) {
      case EPolicyCondition.AlwaysAllow:
        return true;

      case EPolicyCondition.RoleCheck:
      case EPolicyCondition.Authenticated:
        return (this.json.member_of_roles || []).some((role_id) => roles.find(({ id }) => id === role_id));

      case EPolicyCondition.OrganizationOwner:
      case EPolicyCondition.OrganizationMember: {
        let organizationMatcher: null | ((organization: IBasicOrganization) => boolean) = null;

        if (this.json.function_parameters && 'organization_id' in this.json.function_parameters) {
          const resourceOrganizationId =
            resource.params[this.json.function_parameters.organization_id.key.replace(/^:/, '')];

          if (!resourceOrganizationId) {
            throw new Error(
              `Missing organization identifier in resource params for ${resource.method} ${resource.template}`
            );
          }

          organizationMatcher = (organization: IBasicOrganization) => organization.id === resourceOrganizationId;
        } else if (this.json.function_parameters && 'organization_name' in this.json.function_parameters) {
          const resourceOrganizationName =
            resource.params[this.json.function_parameters.organization_name.key.replace(/^:/, '')];

          if (!resourceOrganizationName) {
            throw new Error(`Missing organization name in resource params for ${resource.method} ${resource.template}`);
          }

          organizationMatcher = (organization: IBasicOrganization) => organization.name === resourceOrganizationName;
        }

        if (organizationMatcher === null) {
          organizationMatcher = () => true;
          // some endpoints just require being org member/owner, but the resource doesn't have to be related to the organization
          // throw new Error('Missing organization_id/organization_name in policy definition');
        }

        return (this.json.member_of_roles || []).some((role_id) =>
          roles.find((role) => {
            const roleOrganization = currentOrganization?.name === role.organization_name ? currentOrganization : null;

            return role.id === role_id && roleOrganization && organizationMatcher!(roleOrganization);
            // roleOrganization?.id === resourceOrganizationId;
          })
        );
      }

      case EPolicyCondition.IsSelf: {
        if (!this.json.function_parameters || !('user_id' in this.json.function_parameters)) {
          throw new Error('Missing user_id in policy definition');
        }

        const resourceUserId = resource.params[this.json.function_parameters.user_id.key.replace(/^:/, '')];

        if (!resourceUserId) {
          throw new Error('Missing user identifier in resource params');
        }

        const rolesChecked = (this.json.member_of_roles || []).every((role_id) =>
          roles.find((role) => role.id === role_id)
        );

        return resourceUserId === self?.id && rolesChecked;
      }
    }
  }

  public static examineCollection(
    policies: ResourcePolicy[],
    resource: IResource<any, EResourceMethod>,
    roles: IRole[],
    organization: IBasicOrganization | null,
    self: IBasicUserInfo | null
  ) {
    let currentOperator = policies[0].json.operator;
    let result = currentOperator === EPolicyOperator.And;

    for (const policy of policies) {
      const policyResult = policy.examine(resource, roles, organization, self);
      if (currentOperator === EPolicyOperator.Or) {
        result ||= policyResult;
      } else {
        result &&= policyResult;
      }
      currentOperator = policy.json.operator;
    }

    return result;
  }
}

export const useResourceChecker = () => {
  const { roles, permissions } = useContext(PermissionContext);
  const { organization } = useContext(OrganizationContext);
  const { userInfo } = useContext(CurrentUserContext);

  return useCallback(
    (resource: IResource<any, EResourceMethod> | null) => {
      if (!resource) {
        return false;
      }
      const matchedPermissions = permissions[resource.method].filter((permission) => {
        if (permission.path.includes('*')) {
          const regExp = new RegExp(permission.path.replaceAll('*', '.*'));
          return resource.template.match(regExp);
        }
        return permission.path === resource.template;
      });
      if (matchedPermissions.length === 0) {
        return false;
      }

      return matchedPermissions.some((permission) => {
        const policies = permission.policies.map((policy) => new ResourcePolicy(policy));

        return ResourcePolicy.examineCollection(policies, resource, roles, organization, userInfo);
      });
    },
    [roles, permissions, organization, userInfo]
  );
};

export const useIsResourceAllowed = (resource: IResource<any, EResourceMethod>) => {
  const checker = useResourceChecker();
  return checker(resource);
};

export const ResourceDenied: FC = ({ children }) => {
  return <>{children}</>;
};

export const ResourceGuard: FC<IResourceGuardProps> = ({ resource, children }) => {
  const childrenIfAllowed = useMemo(() => {
    if (Array.isArray(children)) {
      return children.filter((child) => child?.type !== ResourceDenied);
    } else {
      return children;
    }
  }, [children]);

  const childrenIfDenied = useMemo(() => {
    if (Array.isArray(children)) {
      return children.filter((child) => child?.type === ResourceDenied);
    } else {
      return null;
    }
  }, [children]);

  return <>{resource && useIsResourceAllowed(resource) ? childrenIfAllowed : childrenIfDenied}</>;
};

export const resolveResourceUrl = (resource: IResource<any, EResourceMethod>) => {
  return Object.entries(resource.params).reduce(
    (url, [key, value]: [string, any]) => url.replace(`:${key}`, encodeURIComponent(value)),
    resource.template
  );
};

export interface IExecutor<PayloadType, ResponseType> {
  (payload: PayloadType, config?: AxiosRequestConfig): Promise<ResponseType>;
}

type UseExecutorType<ParamsType extends Obj, ResponseType, PayloadType, MethodType extends EResourceMethod> = (
  resource: IResource<ParamsType, MethodType> | null,
  apiExecutor: IApiExecutor
) => IExecutor<PayloadType, ResponseType>;

const defaultUseResourceExecutor = <
  ParamsType extends Obj,
  ResponseType,
  PayloadType,
  MethodType extends EResourceMethod
>(
  resource: IResource<ParamsType, MethodType> | null,
  apiExecutor: IApiExecutor
): IExecutor<PayloadType, ResponseType> => {
  const resourceUrl = resource ? resolveResourceUrl(resource) : '';

  return useCallback(
    async (payload: PayloadType, config?: AxiosRequestConfig) => {
      if (!resource) {
        throw new Error('resource is missing');
      }
      if (resource.method === EResourceMethod.Get) {
        let search: null | URLSearchParams = null;
        if (payload) {
          search = new URLSearchParams();
          Object.entries(payload).forEach(([key, value]) => {
            if (typeof value !== 'undefined') {
              search!.set(key, value.toString());
            }
          });
        }
        if (!config) {
          config = {};
        }
        config.params = search;
        const response = await apiExecutor.get<ResponseType>(resourceUrl, config);
        return response.data;
      } else if (resource.method === EResourceMethod.Post) {
        const response = await apiExecutor.post<ResponseType>(resourceUrl, payload, config);
        return response.data;
      } else if (resource.method === EResourceMethod.Delete) {
        if (!config && payload) {
          config = {
            data: payload,
          };
        }
        const response = await apiExecutor.del<ResponseType>(resourceUrl, config);
        return response.data;
      } else if (resource.method === EResourceMethod.Put) {
        const response = await apiExecutor.put<ResponseType>(resourceUrl, payload, config);
        return response.data;
      } else if (resource.method === EResourceMethod.Patch) {
        const response = await apiExecutor.patch<ResponseType>(resourceUrl, payload, config);
        return response.data;
      } else {
        throw new Error('Unknown resource method');
      }
    },
    [resourceUrl, resource, apiExecutor]
  );
};

const defaultsForUseResource = {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  preparePayload: (...params: any) => ({}),
  useExecutor: defaultUseResourceExecutor,
  useApi,
  apiParams: {},
};

const isEqual = (oldParams: any[], newParams: any[]) => {
  return oldParams.length === newParams.length && oldParams.every((val, index) => val === newParams[index]);
};

type TupleOptional<Type extends any[]> = {
  [K in keyof Type]: Type[K] | undefined;
};

export const getUseResource = <
  InputParamsType extends any[],
  ParamsType extends Obj,
  ResponseType extends any,
  MethodType extends EResourceMethod,
  InputPayloadType extends any[] = [],
  PayloadType = never
>(
  resourceBuilder: IResourceBuilder<InputParamsType, ParamsType, MethodType>,
  prepareResponse: (body: any) => ResponseType,
  preparePayload = defaultsForUseResource.preparePayload as (
    params: ParamsType,
    ...input: InputPayloadType
  ) => PayloadType,
  useExecutor = defaultsForUseResource.useExecutor as UseExecutorType<
    ParamsType,
    ResponseType,
    PayloadType,
    MethodType
  >,
  useApi = defaultsForUseResource.useApi,
  apiParams: IApiConfig = defaultsForUseResource.apiParams
) => {
  return (...params: TupleOptional<InputParamsType>) => {
    const paramsMemo = useMemoWithEqualityCheck(params, isEqual);

    const resource = useMemo(() => {
      if (paramsMemo.some((param) => typeof param === 'undefined')) {
        return null;
      }
      return resourceBuilder(...(paramsMemo as InputParamsType));
    }, [resourceBuilder, paramsMemo]);
    const api = useApi(apiParams);

    const executor = useExecutor(resource, api);

    const executeWithConfig = useCallback(
      (...payload: [...InputPayloadType, AxiosRequestConfig | undefined]): Promise<ResponseType> => {
        if (!resource) {
          throw new Error('resource params missing');
        }
        const justPayload = payload.slice(0, payload.length - 1) as InputPayloadType;
        const preparedPayload = preparePayload(resource.params, ...justPayload);
        return executor(preparedPayload, payload[payload.length - 1]).then(prepareResponse);
      },
      [preparePayload, executor]
    );

    const execute = useCallback(
      (...payload: InputPayloadType): Promise<ResponseType> => {
        return executeWithConfig(...[...payload, void 0]);
      },
      [executeWithConfig]
    );

    const Guard = useMemo<FC>(() => {
      return ({ children }) => <ResourceGuard resource={resource}>{children}</ResourceGuard>;
    }, [resource]);

    const out = useMemo(
      () => ({
        resource,
        execute,
        executeWithConfig,
        Guard,
      }),
      [resource, execute, executeWithConfig, Guard]
    );

    return out;
  };
};
